'use strict';

const libPictProvider = require('pict-provider');
const libMasterListCatalog = require('../master-lists/Headlight-MasterLists.js');

const DETAIL_MOUNT = '#SLC-Detail';
const ENTITY = 'Sample';
// System / bookkeeping columns a user would rarely surface — offered under "Available" but de-emphasized.
const SYSTEM_COLUMN = /^(ID[A-Z]|GUID|CreateDate$|CreatingIDUser$|UpdateDate$|UpdatingIDUser$|Deleted$|DeleteDate$|DeletingIDUser$|ExternalSyncDate$|ExternalSync)/;

/**
 * SampleListColumnsConfig — the bespoke #/SampleListColumnsConfig screen. Lets an admin choose which columns the
 * config app's Samples list (#/PSRS/Sample/List) shows, rename their headers, reorder them, pick which are
 * searchable, and (advanced) set a per-column value template — saved as a cascading MasterListCatalog
 * Configuration (Platform / Customer / My Settings). Gated by the `SampleListColumns-Configure` entitlement.
 *
 * It is a thin UI over the existing MasterListRegistry provider: reads the effective Sample entry (static seed ⊕
 * cascade) from `registry.entries.Sample`, and saves via `registry.saveEntryAsync({Entity:'Sample', Columns,
 * SearchFields}, scope)` — which Puts to Configuration, re-reads the cascade, and applies to the live list without
 * a reload. Passing the Columns array with `PictDashboard.ValueTemplate` intact preserves the Status resolver
 * ({~SampleStatusName:Record.Payload.Status~}); the generic MasterListManager editor drops it, this screen does not.
 */
class SampleListColumnsConfigProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get registry() { return this.pict.providers.MasterListRegistry; }
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.SampleListColumnsConfig)
		{
			this.pict.AppData.SampleListColumnsConfig =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				ScopeLevel: 'Customer', Columns: [],
			};
		}
		return this.pict.AppData.SampleListColumnsConfig;
	}

	/** The entitlement gate — `SampleListColumns-Configure`. Same capability the nav entry checks; server stays authoritative. */
	can()
	{
		const tmpSvc = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpSvc || (typeof tmpSvc.can !== 'function')) { return true; }
		return tmpSvc.can('SampleListColumns', 'Configure');
	}

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_seedEntry() { return libMasterListCatalog.find((pEntry) => pEntry && pEntry.Entity === ENTITY) || {}; }

	/** The effective Sample catalog entry (static seed ⊕ cascade override), from the registry when available. */
	_effectiveEntry()
	{
		const tmpRegistry = this.registry;
		if (tmpRegistry && tmpRegistry.entries && tmpRegistry.entries[ENTITY]) { return tmpRegistry.entries[ENTITY]; }
		return this._seedEntry();
	}

	scopeLevels() { const tmpRegistry = this.registry; return (tmpRegistry && tmpRegistry.constructor && tmpRegistry.constructor.SCOPE_LEVELS) ? tmpRegistry.constructor.SCOPE_LEVELS : [ 'Global', 'Customer', 'User' ]; }
	scopeLabel(pLevel) { return ({ Global: 'Platform Default', Customer: 'Customer', User: 'My Settings' })[pLevel] || pLevel; }
	canWriteScope(pLevel) { const tmpRegistry = this.registry; return (tmpRegistry && typeof tmpRegistry.canWriteScope === 'function') ? tmpRegistry.canWriteScope(pLevel) : (pLevel === 'User'); }

	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		try
		{
			const tmpRegistry = this.registry;
			// Ensure the registry has loaded the cascade (it auto-inits at login; be defensive).
			if (tmpRegistry && typeof tmpRegistry.loadCascadedConfigAsync === 'function' && (!tmpRegistry.entries || !tmpRegistry.entries[ENTITY]))
			{
				await tmpRegistry.loadCascadedConfigAsync();
			}
			// Available columns = the live Sample schema.
			const tmpSchema = await this._getJSON(`${ENTITY}/Schema`);
			const tmpSchemaKeys = (tmpSchema && tmpSchema.properties) ? Object.keys(tmpSchema.properties) : [];
			// Current effective columns + search fields.
			const tmpEntry = this._effectiveEntry();
			const tmpCurrentCols = Array.isArray(tmpEntry.Columns) ? tmpEntry.Columns : [];
			const tmpSearchFields = Array.isArray(tmpEntry.SearchFields) ? tmpEntry.SearchFields : [ 'Name' ];

			const tmpModel = [];
			const tmpSeen = {};
			// Shown columns first, in their configured order.
			for (const tmpCol of tmpCurrentCols)
			{
				const tmpKey = (typeof tmpCol === 'string') ? tmpCol : tmpCol.Key;
				if (!tmpKey || tmpSeen[tmpKey]) { continue; }
				tmpSeen[tmpKey] = true;
				const tmpValueTemplate = (tmpCol && tmpCol.PictDashboard && tmpCol.PictDashboard.ValueTemplate) ? tmpCol.PictDashboard.ValueTemplate : '';
				tmpModel.push({
					Key: tmpKey,
					DisplayName: (typeof tmpCol === 'object' && tmpCol.DisplayName) ? tmpCol.DisplayName : tmpKey,
					ValueTemplate: tmpValueTemplate,
					Shown: true,
					Searchable: tmpSearchFields.indexOf(tmpKey) > -1,
					System: SYSTEM_COLUMN.test(tmpKey),
				});
			}
			// Remaining schema columns as available-to-add.
			for (const tmpKey of tmpSchemaKeys)
			{
				if (tmpSeen[tmpKey]) { continue; }
				tmpSeen[tmpKey] = true;
				tmpModel.push({ Key: tmpKey, DisplayName: tmpKey, ValueTemplate: '', Shown: false, Searchable: false, System: SYSTEM_COLUMN.test(tmpKey) });
			}

			tmpState.Columns = tmpModel;
			tmpState.ScopeLevel = (tmpRegistry && tmpRegistry.defaultScope) ? tmpRegistry.defaultScope : 'Customer';
			if (!this.canWriteScope(tmpState.ScopeLevel))
			{
				const tmpWritable = this.scopeLevels().find((pLevel) => this.canWriteScope(pLevel));
				if (tmpWritable) { tmpState.ScopeLevel = tmpWritable; }
			}
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Error = `Could not load the Samples list configuration: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	// --- editor operations (each re-renders) -----------------------------------------------------------------
	_col(pKey) { return (this.state.Columns || []).find((pCol) => pCol.Key === pKey); }

	toggleShown(pKey)
	{
		const tmpCol = this._col(pKey);
		if (!tmpCol) { return; }
		tmpCol.Shown = !tmpCol.Shown;
		if (!tmpCol.Shown) { tmpCol.Searchable = false; }
		this.state.Message = '';
		this.render();
	}

	setLabel(pKey, pValue) { const tmpCol = this._col(pKey); if (tmpCol) { tmpCol.DisplayName = pValue; } }
	setValueTemplate(pKey, pValue) { const tmpCol = this._col(pKey); if (tmpCol) { tmpCol.ValueTemplate = pValue; } }
	toggleSearch(pKey) { const tmpCol = this._col(pKey); if (tmpCol) { tmpCol.Searchable = !tmpCol.Searchable; this.render(); } }

	_shownIndexes() { const tmpCols = this.state.Columns || []; const tmpOut = []; for (let i = 0; i < tmpCols.length; i++) { if (tmpCols[i].Shown) { tmpOut.push(i); } } return tmpOut; }

	moveUp(pKey)
	{
		const tmpCols = this.state.Columns || [];
		const tmpShown = this._shownIndexes();
		const tmpPos = tmpShown.findIndex((pIdx) => tmpCols[pIdx].Key === pKey);
		if (tmpPos <= 0) { return; }
		const tmpA = tmpShown[tmpPos]; const tmpB = tmpShown[tmpPos - 1];
		const tmpTemp = tmpCols[tmpA]; tmpCols[tmpA] = tmpCols[tmpB]; tmpCols[tmpB] = tmpTemp;
		this.render();
	}
	moveDown(pKey)
	{
		const tmpCols = this.state.Columns || [];
		const tmpShown = this._shownIndexes();
		const tmpPos = tmpShown.findIndex((pIdx) => tmpCols[pIdx].Key === pKey);
		if (tmpPos < 0 || tmpPos >= tmpShown.length - 1) { return; }
		const tmpA = tmpShown[tmpPos]; const tmpB = tmpShown[tmpPos + 1];
		const tmpTemp = tmpCols[tmpA]; tmpCols[tmpA] = tmpCols[tmpB]; tmpCols[tmpB] = tmpTemp;
		this.render();
	}

	setScope(pLevel) { this.state.ScopeLevel = pLevel; this.state.Message = ''; this.render(); }

	async save()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		const tmpRegistry = this.registry;
		if (!tmpRegistry || typeof tmpRegistry.saveEntryAsync !== 'function') { tmpState.Error = 'The list registry is not available.'; this.render(); return; }
		const tmpShown = (tmpState.Columns || []).filter((pCol) => pCol.Shown);
		if (!tmpShown.length) { tmpState.Error = 'Keep at least one column shown.'; this.render(); return; }
		const tmpColumns = tmpShown.map((pCol) =>
		{
			const tmpEntry = { Key: pCol.Key, DisplayName: (pCol.DisplayName || pCol.Key) };
			if (pCol.ValueTemplate) { tmpEntry.PictDashboard = { ValueTemplate: pCol.ValueTemplate }; }
			return tmpEntry;
		});
		const tmpSearchFields = tmpShown.filter((pCol) => pCol.Searchable).map((pCol) => pCol.Key);
		tmpState.Saving = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		try
		{
			await tmpRegistry.saveEntryAsync({ Entity: ENTITY, Columns: tmpColumns, SearchFields: (tmpSearchFields.length ? tmpSearchFields : [ 'Name' ]) }, tmpState.ScopeLevel);
			tmpState.Message = `Saved to ${this.scopeLabel(tmpState.ScopeLevel)}. The Samples list is updated.`;
		}
		catch (pError)
		{
			tmpState.Error = `Save failed: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Saving = false;
			this.render();
		}
	}

	openList() { window.location.hash = '#/PSRS/Sample/List'; }

	// --- render ----------------------------------------------------------------------------------------------
	render()
	{
		if (!this.pict.views.SampleListColumnsConfig) { return; }
		const tmpState = this.state;
		if (!this.can())
		{
			this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, `<div class="slc-empty">You don’t have the <strong>Configure the Samples list columns</strong> entitlement.</div>`);
			return;
		}
		if (tmpState.Loading && !tmpState.Loaded) { this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, '<div class="slc-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>'); return; }

		const tmpScopeBar = this.scopeLevels().map((pLevel) =>
		{
			const tmpDisabled = !this.canWriteScope(pLevel);
			const tmpActive = (tmpState.ScopeLevel === pLevel) ? ' is-active' : '';
			return `<button class="slc-scope-btn${tmpActive}" ${tmpDisabled ? 'disabled title="You cannot save at this scope"' : ''} onclick="_Pict.providers.SampleListColumnsConfig.setScope('${pLevel}')">${this._esc(this.scopeLabel(pLevel))}</button>`;
		}).join('');

		const tmpShownRows = (tmpState.Columns || []).filter((pCol) => pCol.Shown).map((pCol) => this._shownRowHTML(pCol)).join('');
		const tmpAvail = (tmpState.Columns || []).filter((pCol) => !pCol.Shown);
		const tmpAvailHTML = tmpAvail.length
			? tmpAvail.map((pCol) => `<button class="slc-avail${pCol.System ? ' is-system' : ''}" onclick="_Pict.providers.SampleListColumnsConfig.toggleShown('${this._esc(pCol.Key)}')" title="Add ${this._esc(pCol.Key)}"><i class="fas fa-plus"></i> ${this._esc(pCol.Key)}</button>`).join('')
			: '<span class="slc-avail-empty">All columns are shown.</span>';

		const tmpMsg = tmpState.Message ? `<div class="slc-msg">${this._esc(tmpState.Message)}</div>` : '';
		const tmpErr = tmpState.Error ? `<div class="slc-error">${this._esc(tmpState.Error)}</div>` : '';

		const tmpHTML = `
			${tmpErr}${tmpMsg}
			<div class="slc-scopebar"><span class="slc-scope-label">Save to</span>${tmpScopeBar}</div>
			<div class="slc-area-head">Shown columns <span class="slc-hint">— order top-to-bottom; edit the header label; ⚙ sets an advanced value template</span></div>
			<div class="slc-cols">
				<div class="slc-colhead"><span class="slc-ch-order"></span><span class="slc-ch-key">Field</span><span class="slc-ch-label">Header label</span><span class="slc-ch-search">Search</span><span class="slc-ch-actions"></span></div>
				${tmpShownRows}
			</div>
			<div class="slc-area-head">Add a column</div>
			<div class="slc-availwrap">${tmpAvailHTML}</div>
			<div class="slc-savebar">
				<button class="slc-save" ${tmpState.Saving ? 'disabled' : ''} onclick="_Pict.providers.SampleListColumnsConfig.save()">${tmpState.Saving ? 'Saving…' : 'Save columns'}</button>
				<button class="slc-preview" onclick="_Pict.providers.SampleListColumnsConfig.openList()">Preview the list</button>
			</div>`;
		this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, tmpHTML);
	}

	_shownRowHTML(pCol)
	{
		const tmpKey = this._esc(pCol.Key);
		const tmpTemplateBtn = pCol.ValueTemplate
			? `<button class="slc-tmpl-btn is-set" title="Value template: ${this._esc(pCol.ValueTemplate)}" onclick="_Pict.providers.SampleListColumnsConfig._toggleTemplateRow('${tmpKey}')"><i class="fas fa-gear"></i></button>`
			: `<button class="slc-tmpl-btn" title="Set an advanced value template" onclick="_Pict.providers.SampleListColumnsConfig._toggleTemplateRow('${tmpKey}')"><i class="fas fa-gear"></i></button>`;
		const tmpTemplateRow = pCol._showTemplate
			? `<div class="slc-tmpl-row"><input class="slc-tmpl-input" placeholder="e.g. {~SampleStatusName:Record.Payload.Status~}" value="${this._esc(pCol.ValueTemplate)}" oninput="_Pict.providers.SampleListColumnsConfig.setValueTemplate('${tmpKey}', this.value)"><span class="slc-tmpl-hint">Renders the cell; the row value is at <code>Record.Payload.&lt;field&gt;</code>.</span></div>`
			: '';
		return `<div class="slc-col">
			<span class="slc-order">
				<button class="slc-mv" title="Move up" onclick="_Pict.providers.SampleListColumnsConfig.moveUp('${tmpKey}')"><i class="fas fa-chevron-up"></i></button>
				<button class="slc-mv" title="Move down" onclick="_Pict.providers.SampleListColumnsConfig.moveDown('${tmpKey}')"><i class="fas fa-chevron-down"></i></button>
			</span>
			<span class="slc-key" title="${tmpKey}">${tmpKey}</span>
			<input class="slc-label" value="${this._esc(pCol.DisplayName)}" oninput="_Pict.providers.SampleListColumnsConfig.setLabel('${tmpKey}', this.value)">
			<button class="slc-search-btn${pCol.Searchable ? ' is-on' : ''}" title="Toggle searchable" onclick="_Pict.providers.SampleListColumnsConfig.toggleSearch('${tmpKey}')"><i class="fas fa-magnifying-glass"></i></button>
			<span class="slc-col-actions">${tmpTemplateBtn}<button class="slc-remove" title="Hide column" onclick="_Pict.providers.SampleListColumnsConfig.toggleShown('${tmpKey}')"><i class="fas fa-times"></i></button></span>
		</div>${tmpTemplateRow}`;
	}

	_toggleTemplateRow(pKey) { const tmpCol = this._col(pKey); if (tmpCol) { tmpCol._showTemplate = !tmpCol._showTemplate; this.render(); } }
}

module.exports = SampleListColumnsConfigProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'SampleListColumnsConfig',
	AutoInitialize: true,
	AutoInitializeOrdinal: 231,
};
